/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import MailSendIcon from 'src/components/icons/MailSendIcon'
import MailResendIcon from 'src/components/icons/MailResendIcon'
import { Modal, Alert, App } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useState, useEffect, useMemo } from 'react'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { TextInput } from 'src/components/ts-components/TextInput'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { t } from '@sportninja/common/i18n'

type InviteInfo = {
  is_invited: boolean
  is_accepted: boolean
  invitation_id?: string
}

type UserInfo = {
  id: string
  name_first: string
  name_last: string
  email?: string
  official_type_id?: string
  role_type_id?: string
  team_official_type_id?: string
  player_type_id?: string
}

type SendInvitationModalProps = {
  isOpen: boolean
  onClose: () => void
  invite: InviteInfo
  user: UserInfo
  slug: string
  onComplete: () => void | Promise<void>
}

/**
 * Modal for sending or resending invitations
 * Matches the styling of DeleteConfirmationModal from UserSettingsModal
 */
const SendInvitationModal = ({
  isOpen,
  onClose,
  invite,
  user,
  slug,
  onComplete,
}: SendInvitationModalProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { message } = App.useApp()

  // Determine mode
  const isResendMode = invite.is_invited && !invite.is_accepted

  // Email form state
  const [email, setEmail] = useState(user.email || '')

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form validation state
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState<Record<string, string>>({})

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail(user.email || '')
      setError(null)
      setFieldErrors([])
      setCustomError({})
    }
  }, [isOpen, user.email])

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    setEmail(user.email || '')
    setError(null)
    setFieldErrors([])
    setCustomError({})
    setIsLoading(false)
    onClose()
  }, [onClose, user.email])

  /**
   * Handles send invitation
   */
  const handleSendInvitation = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    setFieldErrors([])
    setCustomError({})

    try {
      const body = {
        email,
        official_type_id: user.official_type_id,
        role_type_id: user.role_type_id,
        team_official_type_id: user.team_official_type_id,
        player_type_id: user.player_type_id,
        name_first: user.name_first,
        name_last: user.name_last,
      }

      await req(slug, {
        method: 'PUT',
        body: JSON.stringify(body),
      })

      // Show success message using Ant Design message
      message.success(t('Web:invitationSentSuccessfully', {}))

      await onComplete()
      handleClose()
    } catch (e: any) {
      if (e?.invalid_fields) {
        const fields = Object.keys(e.invalid_fields)
        setFieldErrors(fields)
        setCustomError(e.invalid_fields)
        return
      }
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to send invitation')
    } finally {
      setIsLoading(false)
    }
  }, [email, user, slug, onComplete, handleClose, message])

  /**
   * Handles resend invitation
   */
  const handleResendInvitation = useCallback(async () => {
    const invitationId = invite.invitation_id || user.id
    setIsLoading(true)
    setError(null)

    try {
      await req(`/invitations/${invitationId}/resend`)

      // Show success message using Ant Design message
      message.success('Invitation resent successfully')

      await onComplete()
      handleClose()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      setError(errorMessage || 'Failed to resend invitation')
      setIsLoading(false)
    }
  }, [invite.invitation_id, user.id, onComplete, handleClose, message])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  const modalTitle = isResendMode
    ? 'Resend Invitation'
    : `${t('Web:sendAnInvite', {})} to ${user.name_first} ${user.name_last}`

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Mail Icon */}
        {isResendMode ? (
          <MailResendIcon size={48} color={colors.PRIMARY} />
        ) : (
          <MailSendIcon size={48} color={colors.PRIMARY} />
        )}

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          {modalTitle}
        </Text>

        {/* Content based on mode */}
        {isResendMode ? (
          // Resend Mode Content
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              Resend an invitation to{' '}
              <span
                css={css`
                  font-weight: 700;
                `}
              >
                {user.name_first} {user.name_last}
              </span>
              ?
            </Text>
          </div>
        ) : (
          // Send Mode Content
          <div
            css={css`
              align-self: stretch;
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <div
              css={css`
                text-align: center;
              `}
            >
              <Text
                variant='paragraphMedium'
                weight='regular'
                color={colors.WHITE}
              >
                {t('Web:enterEmailToInvite', {})}
              </Text>
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style={`
                  margin-top: 8px;
                  display: block;
                `}
              >
                {t('Web:invitedToRegister', {})}
              </Text>
            </div>

            {/* Email Input */}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <TextInput
                name='email'
                value={email}
                setValue={setEmail}
                label={t('common:email', {})}
                type='email'
                placeholder='email@example.com'
                autoComplete='off'
                fieldErrors={fieldErrors}
                customError={customError}
                required
              />
            </div>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          {isResendMode ? (
            <>
              <Button
                text={isLoading ? 'Resending...' : 'Resend'}
                onClick={handleResendInvitation}
                variant='primary'
                disabled={isLoading}
                isLoading={isLoading}
              />
              <Button
                text='Cancel'
                onClick={handleClose}
                variant='secondary'
                disabled={isLoading}
              />
            </>
          ) : (
            <>
              <Button
                text={isLoading ? 'Sending...' : 'Send'}
                onClick={handleSendInvitation}
                variant='primary'
                disabled={isLoading || !email}
                isLoading={isLoading}
              />
              <Button
                text='Cancel'
                onClick={handleClose}
                variant='secondary'
                disabled={isLoading}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Trigger button component for SendInvitationModal
 * Renders the mail icon button and manages modal state
 */
type SendInvitationTriggerProps = {
  invite: InviteInfo
  user: UserInfo
  slug: string
  onComplete: () => void | Promise<void>
  disabled?: boolean
}

export const SendInvitationTrigger = ({
  invite,
  user,
  slug,
  onComplete,
  disabled = false,
}: SendInvitationTriggerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Don't render if invitation is already accepted
  if (invite.is_invited && invite.is_accepted) {
    return null
  }

  const isResendMode = invite.is_invited && !invite.is_accepted

  return (
    <>
      <button
        type='button'
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        css={css`
          background: transparent;
          border: none;
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          padding: 4px 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: ${disabled ? 0.5 : 1};
          &:hover {
            opacity: ${disabled ? 0.5 : 0.8};
          }
        `}
        title={isResendMode ? 'Resend Invitation' : 'Send Invitation'}
      >
        {isResendMode ? (
          <MailResendIcon size={24} color={colors.PRIMARY} />
        ) : (
          <MailSendIcon size={24} color={colors.PRIMARY} />
        )}
      </button>
      <SendInvitationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        invite={invite}
        user={user}
        slug={slug}
        onComplete={onComplete}
      />
    </>
  )
}

export default SendInvitationModal
